import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import { ASSETS } from '../../Public/assets-data.js';
import { getLoginData } from '../../Public/login-data.js';

function formatSecs(secs) {
    if (secs <= 0) return '0';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}`;
}

export default function SessionExpiryModal({ show, secsLeft, onContinue, onLogout }) {
    const { language = 'en', dir } = useSelector((s) => s.systemData || {});
    const txt = getLoginData(language);
    const MODAL_SECS = 10 * 60;
    const pct    = Math.min(100, ((secsLeft ?? 0) / MODAL_SECS) * 100);
    const urgent = (secsLeft ?? 0) <= 3 * 60;

    return (
        <AnimatePresence>
            {show && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="session-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
                    />

                    {/* Card */}
                    <motion.div
                        key="session-modal"
                        initial={{ opacity: 0, scale: 0.88, y: 24 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: 16 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                        className="fixed inset-0 z-[9999] flex items-center justify-center p-6 pointer-events-none"
                    >
                        <div dir={dir} className="pointer-events-auto w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">

                            {/* Logo header */}
                            <div className="relative bg-gradient-to-b from-primary-500/10 to-white flex flex-col items-center pt-8 pb-4 px-6">
                                <motion.img
                                    src={ASSETS.LOGO_NOTEXT}
                                    alt=""
                                    aria-hidden="true"
                                    role="presentation"
                                    className="w-24 h-24 object-contain drop-shadow-md"
                                    animate={{ y: [0, -6, 0] }}
                                    transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
                                />

                                {/* Countdown */}
                                <span className={`mt-3 text-3xl font-bold font-mono tabular-nums ${urgent ? 'text-secondary-500' : 'text-primary-500'}`}>
                                    {formatSecs(secsLeft)}
                                </span>
                            </div>

                            {/* Text */}
                            <div className="px-6 pb-2 text-center">
                                <h2 className="text-xl font-extralight font-secondary text-primary-500">{txt.sessionModalTitle}</h2>
                                <p className="text-sm text-gray-500 font-primary mt-1">{txt.sessionModalBody}</p>
                            </div>

                            {/* Progress bar */}
                            <div className="mx-6 mt-3 h-1 rounded-full bg-gray-100 overflow-hidden">
                                <motion.div
                                    className={`h-full rounded-full ${urgent ? 'bg-secondary-500' : 'bg-primary-500'}`}
                                    style={{ width: `${pct}%` }}
                                    transition={{ duration: 1, ease: 'linear' }}
                                />
                            </div>

                            {/* Buttons */}
                            <div className="px-6 py-5 flex flex-col gap-2.5">
                                <button
                                    onClick={onContinue}
                                    className="w-full py-3 rounded-2xl bg-primary-500 text-white text-sm font-bold font-primary hover:opacity-90 active:scale-[0.98] transition-all"
                                >
                                    {txt.sessionExtend}
                                </button>
                                <button
                                    onClick={onLogout}
                                    className="w-full py-2.5 rounded-2xl text-gray-400 text-sm font-primary hover:text-gray-600 transition-colors"
                                >
                                    {txt.sessionLogout}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
